import { useShell } from '../provider';
import type { HeaderNotificationItem as HeaderNotificationItemData } from './notification-types';

type HeaderNotificationItemProps = {
  item: HeaderNotificationItemData;
  isMutating?: boolean;
  onSelect?: (item: HeaderNotificationItemData) => void;
};

function renderMessage(message: string) {
  return message.split(/(\*\*[^*]+\*\*)/).map((part, index) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={index} className="font-semibold">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    )
  );
}

export function HeaderNotificationItem({
  item,
  isMutating,
  onSelect,
}: HeaderNotificationItemProps) {
  const { navigation } = useShell();
  const LinkComponent = navigation?.LinkComponent ?? 'a';
  const canMarkRead = Boolean(item.unread && onSelect);
  const label = item.message.replaceAll('**', '');
  const content = (
    <>
      {item.avatarSrc ? (
        // Avatar URLs come from multiple runtime domains, so they cannot rely on Next image allowlists.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={item.avatarSrc}
          alt=""
          width={48}
          height={48}
          loading="lazy"
          decoding="async"
          className="bg-surface-variant size-12 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span
          aria-hidden
          className="bg-primary-variant text-primary flex size-12 shrink-0 items-center justify-center rounded-full text-lg font-semibold"
        >
          {item.fallbackInitial}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="text-on-surface leading-xs block text-xs">
          {renderMessage(item.message)}
        </span>
        <span className="text-on-surface-variant leading-xs mt-1 flex items-center gap-2 text-xs">
          <span>{item.date}</span>
          <span
            aria-hidden
            className="bg-outline-variant size-1 rounded-full"
          />
          <span>{item.category}</span>
        </span>
      </span>
      {item.unread ? (
        <span
          aria-label="Chưa đọc"
          className="bg-primary size-2.5 shrink-0 rounded-full"
        />
      ) : null}
    </>
  );

  if (item.href) {
    return (
      <LinkComponent
        href={item.href}
        onClick={() => onSelect?.(item)}
        aria-label={`Mở thông báo: ${label}`}
        className="hover:bg-surface-variant focus-visible:ring-primary/50 flex min-h-[82px] w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {content}
      </LinkComponent>
    );
  }

  if (canMarkRead) {
    return (
      <button
        type="button"
        disabled={isMutating}
        onClick={() => onSelect?.(item)}
        aria-label={`Đánh dấu đã đọc: ${label}`}
        className="hover:bg-surface-variant focus-visible:ring-primary/50 flex min-h-[82px] w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
      >
        {content}
      </button>
    );
  }

  return (
    <article
      className="flex min-h-[82px] items-center gap-3 rounded-2xl px-4 py-3"
      aria-label={label}
    >
      {content}
    </article>
  );
}
